import { ref, computed } from "vue";
import { useCatalogServiceStore } from "@domains/service/stores/catalog-service.store";
import { useItineraryStore } from "@domains/itinerary/stores/itinerary.store";
import { itineraryRepository } from "@domains/itinerary/repositories/itinerary.repository";
import { providerApi } from "@infrastructure/api/provider.api";
// =============================================================================
// COMPOSABLE
// =============================================================================
/**
 * Orchestrates fetching of providers, services, and itineraries needed for
 * the NewConversationModal and ConversationFilters popover.
 *
 * - Catalog services and itineraries delegate to their Pinia stores (5-min cache).
 * - Providers use a local guard since no provider store exists yet.
 * - All numeric IDs are converted to strings to satisfy CreateConversationPayload.
 */
export function useConversationFormData() {
    const catalogServiceStore = useCatalogServiceStore();
    const itineraryStore = useItineraryStore();
    const rawProviders = ref([]);
    const providerBookingItineraries = ref([]);
    const isLoadingProviders = ref(false);
    const isLoadingProviderBookings = ref(false);
    const hasFetchedProviders = ref(false);
    const hasFetchedProviderBookings = ref(false);
    const useProviderBookingSource = ref(false);
    // ---------------------------------------------------------------------------
    // COMPUTED — mapped views
    // ---------------------------------------------------------------------------
    const providers = computed(() => rawProviders.value);
    const services = computed(() => catalogServiceStore.services.map((s) => ({
        id: String(s.id),
        name: s.name,
        provider_id: s.provider?.id != null
            ? String(s.provider.id)
            : s.provider_id != null
                ? String(s.provider_id)
                : "",
    })));
    const sourceItineraries = computed(() => useProviderBookingSource.value
        ? providerBookingItineraries.value
        : itineraryStore.itineraries);
    function asStringId(...values) {
        const value = values.find((candidate) => {
            return candidate !== null && candidate !== undefined && candidate !== "";
        });
        return value === null || value === undefined ? "" : String(value);
    }
    function isActiveBookingSlot(slot) {
        return slot.is_active !== false && slot.fulfillment_status !== "CANCELLED";
    }
    function mapSlotToOption(slot) {
        if (!isActiveBookingSlot(slot))
            return null;
        const serviceId = asStringId(slot.service?.id, slot.service_id);
        const bookingId = asStringId(slot.booking_id, slot.id);
        if (!serviceId || !bookingId)
            return null;
        const serviceName = slot.service?.name ??
            slot.service_name ??
            slot.service_name_snapshot ??
            `Servicio ${serviceId}`;
        const providerId = asStringId(slot.service?.provider?.id, slot.service?.provider_id, slot.provider_id);
        return {
            id: bookingId,
            service_id: serviceId,
            service_name: serviceName,
            ...(providerId ? { provider_id: providerId } : {}),
            booking_id: bookingId,
            slot_id: asStringId(slot.service_slot_id, slot.service_slot?.id),
        };
    }
    function getBookingRows(itinerary) {
        const slots = itinerary.slots_itineraries;
        if (Array.isArray(slots) && slots.length > 0) {
            return slots;
        }
        const bookings = itinerary.bookings;
        return Array.isArray(bookings) ? bookings : [];
    }
    const itineraries = computed(() => sourceItineraries.value.map((it) => ({
        id: String(it.id),
        code: it.code,
        slots: getBookingRows(it)
            .map((slot) => mapSlotToOption(slot))
            .filter((slot) => slot !== null),
    })));
    const isLoading = computed(() => catalogServiceStore.isLoading ||
        itineraryStore.isLoading ||
        isLoadingProviders.value ||
        isLoadingProviderBookings.value);
    // ---------------------------------------------------------------------------
    // ACTIONS
    // ---------------------------------------------------------------------------
    async function fetchProviders() {
        if (hasFetchedProviders.value)
            return;
        isLoadingProviders.value = true;
        try {
            const { data } = await providerApi.getAll();
            rawProviders.value = data.map((p) => ({
                id: String(p.id),
                name: p.name,
            }));
            hasFetchedProviders.value = true;
        }
        finally {
            isLoadingProviders.value = false;
        }
    }
    async function fetchProviderBookings() {
        if (hasFetchedProviderBookings.value)
            return;
        isLoadingProviderBookings.value = true;
        try {
            providerBookingItineraries.value =
                await itineraryRepository.getProviderBookings();
            hasFetchedProviderBookings.value = true;
        }
        finally {
            isLoadingProviderBookings.value = false;
        }
    }
    /** Idempotent — stores handle their own cache; raw providers are opt-in legacy data. */
    async function load(options = {}) {
        const includeProviders = options.includeProviders === true;
        const useProviderBookings = options.providerBookings === true;
        useProviderBookingSource.value = useProviderBookings;
        await Promise.all([
            catalogServiceStore.fetchServices(options.forceRefreshServices === true),
            useProviderBookings
                ? fetchProviderBookings()
                : itineraryStore.fetchAll(options.forceRefreshItineraries === true),
            includeProviders ? fetchProviders() : Promise.resolve(),
        ]);
    }
    return { providers, services, itineraries, isLoading, load };
}
